import { useEffect, useState } from "react";
import { BUTTON_CONNECT_TIKTOK } from "../../i18n/keysTranslations";
import {
  BUTTON_TIKTOK_ACTION_ID,
  MODE_BACK_DROP_CONNECT,
  TIKTOK,
} from "../../utils/constants";
import Avatar from "@mui/material/Avatar";
import { logoPlatform } from "../../utils/string";
import { useTranslationApp } from "../../lib/i18next";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import { startConnectTiktok, startGetTokenTiktok } from "../../actions/user";
import IconButton from "./IconButton";
import Loader from "../Loaders/Loader";
import AddIcon from "@mui/icons-material/Add";
import BackDropLoader from "../Loaders/BackDropLoader";
import { createPortal } from "react-dom";
import Button from "./Button";

const ButtonTikTokAction = ({
  id = BUTTON_TIKTOK_ACTION_ID,
  text = BUTTON_CONNECT_TIKTOK,
  prevBlockClick = false,
  onPrevBlockClick = () => {},
  onClick = () => {},
  onCallbackOpenPopup = () => {},
  disabled = false,
  sx = {},
  logo = TIKTOK,
  showLogo = true,
  type = "button",
  mode = "verify",
  icon = <AddIcon />,
  disableRipple = false,
  showButtonIcon = false,
  doTranslation = true,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [openBackDrop, setOpenBackDrop] = useState(false);
  const [externalPopup, setExternalPopup] = useState(null);
  const [modeBackDrop, setModeBackDrop] = useState();

  const dispatch = useDispatchApp();
  const integrations = useSelectorApp((state) => state?.user?.integrations);
  const tikTokIntegrations = integrations?.tiktok || {};
  const isTikTokConnected = Object.values(tikTokIntegrations).some(
    (integration) => integration.isActive
  );
  const { t } = useTranslationApp();

  const onConnectAccount = async () => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_POPUP);
    dispatch(
      startConnectTiktok({
        onChangeLoading: (loading) => {
          setIsLoading(loading);
          setOpenBackDrop(loading);
          if (!loading) {
            onCallbackOpenPopup();
          }
        },
        onChangePopupReference: (popup) => setExternalPopup(popup),
      })
    );
  };

  const onGetTokenTiktok = async ({ code, state }) => {
    setModeBackDrop(MODE_BACK_DROP_CONNECT.GET_INFORMATION);
    setOpenBackDrop(true);
    const response = await dispatch(
      startGetTokenTiktok({ code, state, withAuth: true })
    );
    setOpenBackDrop(false);

    if (response.ok) {
      await onClick();
    }
  };

  useEffect(() => {
    if (!externalPopup) {
      return;
    }

    const timer = setInterval(() => {
      if (!externalPopup || externalPopup.closed) {
        clearInterval(timer);
        setExternalPopup(null);
        return;
      }

      try {
        const currentUrl = externalPopup.location.href;
        if (!currentUrl) {
          return;
        }
        const searchParams = new URL(currentUrl).searchParams;
        const authCode =
          searchParams.get("auth_code") || searchParams.get("code");
        const state = searchParams.get("state");

        if (authCode && state) {
          externalPopup.close();
          setExternalPopup(null);
          clearInterval(timer);

          onGetTokenTiktok({
            code: authCode,
            state,
          });
        }
      } catch {
        if (externalPopup.closed) {
          clearInterval(timer);
          setExternalPopup(null);
        }
      }
    }, 500);

    return () => {
      timer && clearInterval(timer);
    };
    // eslint-disable-next-line
  }, [externalPopup]);

  if (type === "icon") {
    return (
      <>
        {openBackDrop &&
          createPortal(
            <BackDropLoader
              open={openBackDrop}
              platform={TIKTOK}
              from={modeBackDrop}
            />,
            document.body
          )}
        <IconButton
          id={id}
          disableRipple={disableRipple}
          sx={{ ...sx }}
          onClick={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (prevBlockClick) {
              onPrevBlockClick();
              return;
            }

            if ((!isTikTokConnected && mode === "verify") || mode === "add") {
              await onConnectAccount();
              return;
            }

            setIsLoading(true);
            onClick();
            setIsLoading(false);
          }}
        >
          {isLoading ? <Loader hasMarginTop={false} size={20} /> : icon}
        </IconButton>
      </>
    );
  }

  return (
    <>
      {openBackDrop &&
        createPortal(
          <BackDropLoader
            open={openBackDrop}
            platform={TIKTOK}
            from={modeBackDrop}
          />,
          document.body
        )}
      <Button
        id={id}
        disabled={disabled}
        loading={isLoading}
        color="secondary"
        sx={{
          ...sx,
        }}
        onClick={async (e) => {
          e.preventDefault();
          if (prevBlockClick) {
            onPrevBlockClick();
            return;
          }

          if ((!isTikTokConnected && mode === "verify") || mode === "add") {
            await onConnectAccount();
            return;
          }

          setIsLoading(true);
          await onClick();
          setIsLoading(false);
        }}
      >
        {showButtonIcon && icon}
        {showLogo && (
          <Avatar
            src={logoPlatform(logo)}
            variant="square"
            sx={{
              width: "20px",
              height: "20px",
              mr: 1,
              ".MuiAvatar-img": {
                objectFit: "contain",
              },
            }}
          />
        )}
        {doTranslation ? t(text) : text}
      </Button>
    </>
  );
};

export default ButtonTikTokAction;
